/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit2, Trash2, ChevronDown, X, Search as SearchIcon, Check, Eye } from 'lucide-react';
import apiClient from '../api/client';
import { industryConfig } from '../components/PhonePreview';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import toastService from '../utils/toastService';
import PermissionGuard from '../components/PermissionGuard';
import ContextWarning from '../components/ContextWarning';
import { getIndustryLabel } from '../constants/workflowOptions';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const industries = [
  { value: 'automotive', label: 'Automotive' },
  { value: 'aviation', label: 'Aviation' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'field_services', label: 'Field Services' },
  { value: 'general', label: 'General' }
];

// Named constants 
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

const Templates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');
  const [industryFilter, setIndustryFilter] = useState('All');
  const [isIndustryOpen, setIsIndustryOpen] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');
  const industryDropdownRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const fetchWorkflows = useCallback(async (params = {}) => {

    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (params.search) { queryParams.append('search', params.search); }
      if (params.industry && params.industry !== 'All') { queryParams.append('industry', params.industry); }
      if (params.page) { queryParams.append('page', params.page); }


      const response = await apiClient.get(`/workflows?${queryParams.toString()}`);
      const data = response.data;
      if (Array.isArray(data)) {
        setWorkflows(data);
        setTotalPages(1);
      } else {
        setWorkflows(data.workflows || []);
        setTotalPages(data.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
      setError('Failed to load workflows. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const isSearchTooShort = searchQuery.trim().length > 0 && searchQuery.trim().length < SEARCH_MIN_LENGTH;

    const handler = setTimeout(() => {
      fetchWorkflows({
        search: isSearchTooShort ? '' : searchQuery,
        industry: industryFilter,
        page: currentPage
      });
      if (!isSearchTooShort) {setSearchError('');}
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [searchQuery, industryFilter, currentPage, fetchWorkflows]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(event.target)) {
        setIsIndustryOpen(false);
        setIndustrySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredIndustries = industries.filter(ind =>
    ind.label.toLowerCase().includes(industrySearch.toLowerCase())
  );

  const handleDelete = async (workflow) => {
    setDeleting(true);
    setDeleteError('');
    try {
      await apiClient.delete(`/workflows/${workflow.id}`);
      setDeleteConfirm(null);
      toastService.success('Workflow deleted successfully');
      fetchWorkflows();
    } catch (err) {
      const msg = err.response?.data?.message;
      setDeleteError(Array.isArray(msg) ? msg[0] : (msg || 'Failed to delete workflow'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = (workflow) => {
    setDeleteConfirm(workflow);
  };

  const handleEdit = (workflow) => {
    navigate(`/workflow/${workflow.id}/edit`);
  };

  const handleView = (workflow) => {
    navigate(`/workflow/${workflow.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="page-title">Capture Workflows</h1>
          <p className="page-subtitle">Create and manage your custom capture processes</p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-64">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/30" size={16} />
            <input
              type="text"
              placeholder="Search..."
              className={`w-full pl-10 pr-4 py-2.5 bg-base-100 border rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all shadow-sm ${searchError ? 'border-error ring-1 ring-error/20' : 'border-base-300'}`}
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
                if (e.target.value.trim().length >= 3 || e.target.value.trim().length === 0) {
                  setSearchError('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim().length > 0 && searchQuery.trim().length < 3) {
                  setSearchError('Search must be at least 3 characters');
                }
              }}
            />
            {searchError && (
              <div className="absolute top-full left-0 mt-1 text-[10px] text-error font-black uppercase tracking-widest">{searchError}</div>
            )}
          </div>

          <div className="relative w-full sm:w-48" ref={industryDropdownRef}>
            <button
              onClick={() => setIsIndustryOpen(!isIndustryOpen)}
              className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-2.5 bg-base-100 border border-base-300 text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary outline-none cursor-pointer group shadow-sm transition-all"
            >
              <span className="truncate flex-1 text-left">
                {industryFilter === 'All' ? 'All Industries' : industries.find(i => i.value === industryFilter)?.label ?? industryFilter}
              </span>
              <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isIndustryOpen ? 'rotate-180' : ''}`} />
            </button>

            {isIndustryOpen && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-300 rounded-xl shadow-2xl z-[110] overflow-hidden animate-fade-in flex flex-col max-h-72">

                <div className="overflow-y-auto no-scrollbar flex-1">
                  <button
                    onClick={() => {
                      setIndustryFilter('All');
                      setIsIndustryOpen(false);
                      setIndustrySearch('');
                      setCurrentPage(1);
                    }}
                    className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-300 flex items-center gap-2 group ${industryFilter === 'All' ? 'bg-primary/5' : ''}`}
                  >
                    <span className={`text-[11px] font-black uppercase tracking-widest ${industryFilter === 'All' ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>All Industries</span>
                    {industryFilter === 'All' && <Check size={12} className="text-primary ml-auto" />}
                  </button>

                  {filteredIndustries.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">No matching results</p>
                    </div>
                  ) : (
                    filteredIndustries.map((ind) => (
                      <button
                        key={ind.value}
                        onClick={() => {
                          setIndustryFilter(ind.value);
                          setIsIndustryOpen(false);
                          setIndustrySearch('');
                          setCurrentPage(1);
                        }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group ${industryFilter === ind.value ? 'bg-primary/5' : ''}`}
                      >
                        <span className={`text-[11px] font-black uppercase tracking-widest ${industryFilter === ind.value ? 'text-primary' : ' group-hover:text-primary'}`}>{ind.label}</span>
                        {industryFilter === ind.value && <Check size={12} className="text-primary ml-auto" />}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <PermissionGuard permissions={['templates.create']}>
            {user?.subAccountUID && (
              <button
                onClick={() => navigate('/workflow/new')}
                className="btn-premium w-full sm:w-auto h-[42px] px-6"
              >
                <Plus size={18} />
                <span>New Workflow</span>
              </button>
            )}
          </PermissionGuard>

        </div>
      </div>

      <ContextWarning title={`Select ${SUB_ACCOUNT_LABEL.withArticle} to create or edit workflows`} message={`You're viewing all ${ACCOUNT_LABEL.pluralLower}. To create, edit, or delete a workflow, select ${SUB_ACCOUNT_LABEL.withArticle} from the switcher in the bottom left`} />

      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl mb-4 text-sm font-bold">{error}</div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 space-y-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">Loading workflows...</p>
        </div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-24 glass-panel rounded-3xl border-dashed border-2 border-base-300">
          <div className="text-5xl mb-4 opacity-20">🔍</div>
          <h3 className="text-xl font-black text-base-content mb-2 tracking-tight uppercase">No matches found</h3>
          <p className="text-base-content/50 max-w-xs mx-auto font-bold uppercase tracking-widest text-[10px]">We couldn't find any workflows matching your current search or filters.</p>
          <button
            onClick={() => { setSearchQuery(''); setIndustryFilter('All'); }}
            className="mt-6 text-primary font-black uppercase tracking-widest text-[10px] hover:underline"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {workflows.map(workflow => (
            <div key={workflow.id} className="bg-base-100 border border-base-300 rounded-2xl p-6 group hover:border-primary/50 transition-all duration-300 relative shadow-sm hover:shadow-xl hover:-translate-y-1">
              <div className="flex items-start justify-between mb-4">
                <div className="text-3xl filter drop-shadow-sm">{(industryConfig[workflow.industry] || industryConfig.general).icon}</div>

                <div className="flex gap-2 ">
                  <button
                    onClick={() => handleView(workflow)}
                    className="p-2 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-xl transition-colors"
                    title="View Workflow"
                  >
                    <Eye size={16} />
                  </button>

                  {!workflow.inUse && (
                    <PermissionGuard permissions={['templates.update']}>
                      <button
                        onClick={() => handleEdit(workflow)}
                        className="p-2 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-xl transition-colors"
                        title="Edit Workflow"
                      >
                        <Edit2 size={16} />
                      </button>
                    </PermissionGuard>
                  )}

                  {!workflow.hasActiveAssignments && (
                    <PermissionGuard permissions={['templates.delete']}>
                      <button
                        onClick={() => handleDeleteClick(workflow)}
                        className="p-2 text-base-content/40 hover:text-error hover:bg-error/10 rounded-xl transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </PermissionGuard>
                  )}

                </div>
              </div>

              <h3 className="font-black text-base-content text-lg mb-1 tracking-tight truncate">{workflow.name}</h3>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-base-200 text-base-content/60 text-[10px] font-black uppercase tracking-widest rounded-full mb-4">
                <div className="w-1 h-1 rounded-full bg-primary animate-pulse" />
                {getIndustryLabel(workflow.industry)}
              </div>

              <div className="flex items-center justify-between text-[11px] font-bold text-base-content/30 uppercase tracking-widest border-t border-base-300 pt-4">
                <div className="flex items-center gap-1">
                  <span className="text-base-content/60">{workflow.itemCount}</span>
                  <span>Steps</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>{new Date(workflow.createdAt).toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="px-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          loading={loading}
        />
      </div>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => { setDeleteConfirm(null); setDeleteError(''); }}
        title="Delete Workflow"
      >
        <div className="mb-6">
          {deleteError && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 text-error rounded-xl text-xs font-bold flex items-center gap-2 animate-in fade-in slide-in-from-top-1">
              <X size={14} className="shrink-0" />
              <span>{deleteError}</span>
            </div>
          )}
          {deleteConfirm?.hasActiveAssignments ? (
            <div className="bg-error/10 border border-error/20 p-4 rounded-xl mb-4">
              <p className="text-error text-sm font-bold flex items-center gap-2">
                <X size={16} className="shrink-0" />
                <span>This workflow cannot be deleted because it is associated with active or completed assignments.</span>
              </p>
            </div>
          ) : deleteConfirm?.inUse ? (
            <p className="text-base-content/60 text-sm">
              This workflow is associated with one or more assignments. Are you sure you want to delete it?
            </p>
          ) : (
            <p className="text-base-content/60 text-sm">
              Are you sure you want to delete the <strong className="text-base-content font-bold">{deleteConfirm?.name}</strong> workflow? This action cannot be undone.
            </p>
          )}
        </div>

        <div className="flex gap-3">
          {deleteConfirm?.hasActiveAssignments ? (
            <button
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 px-4 py-2.5 bg-base-200 text-base-content rounded-xl font-bold text-sm hover:bg-base-300 transition"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Workflow'}
              </button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Templates;
